import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getInstanceOfUserEmailService, getInstanceOfUserService } from "src/dependencies";
import { UserEmailService, UserService } from "src/services";
import { ROUTES_CONFIG, queryKeys } from "src/constants";
import { Role, Wrapper, WrapperProps, Authorisation } from "src/types";
import { AuthorisationContext } from "src/context";
import { StatusModals } from "src/components";

interface AuthorisationProviderProps extends WrapperProps {
  userService: UserService;
  userEmailService: UserEmailService;
  roleRequired: Role | Role[];
  emailVerificationRequired?: boolean;
}

const AuthorisationProvider: Wrapper<AuthorisationProviderProps> = ({
  children,
  userService,
  userEmailService,
  roleRequired,
  emailVerificationRequired
}) => {
  const navigate = useNavigate();

  const {
    data: authorisationData,
    error,
    refetch
  } = useQuery<Authorisation>({
    queryKey: queryKeys.authorisation(roleRequired, emailVerificationRequired),
    queryFn: async () => {
      const authorisation = await userService.checkAuthorisation(roleRequired);

      if (emailVerificationRequired && authorisation.authStatus === "AUTHORISED") {
        const emailVerificationStatus = await userEmailService.checkUserEmailVerificationStatus();

        if (emailVerificationStatus !== "VERIFIED") {
          navigate(ROUTES_CONFIG.userEmailVerification.path);
          authorisation.authStatus = "EMAIL_VERIFICATION_REQUIRED";
        }
      }

      if (authorisation.authStatus === "UNAUTHORISED" || authorisation.userRole === "NONE") {
        navigate(ROUTES_CONFIG.forbidden.path);
      }

      return authorisation;
    },
    retry: false
  });

  const { authStatus, userRole } = authorisationData ?? {};

  if (error) {
    return <StatusModals.AuthorisationError onRetry={refetch} />;
  }

  return (
    <AuthorisationContext.Provider value={{ userRole: userRole || "NONE", authStatus: authStatus || "UNAUTHORISED" }}>
      {children}
    </AuthorisationContext.Provider>
  );
};

// DI
export default (props: Omit<AuthorisationProviderProps, "userService" | "userEmailService">) => (
  <AuthorisationProvider
    userService={getInstanceOfUserService()}
    userEmailService={getInstanceOfUserEmailService()}
    {...props}
  />
);
